import { defineStore } from "pinia";
import { COMMON_CONSTANTS } from "~/constants/common-constants";
import type { IPlaceOrderResponse } from "~/types/Cart";
import type { IOrderSummary, FilterState } from "~/types/order";
import type { OrderHistoryPayload } from "~/types/OrderHistory";

const getInitialData = (): IOrderSummary => ({
  sub_total: "",
  code_type: "",
  currency_type: "",
  discount_amount: "",
  total_amount: "",
  remaining_balance: 0,
  code_applied: "",
  sub_total_inr: 0,
  discount_amount_inr: 0,
  total_amount_inr: 0,
  applied_code: "",
  is_cashback_coupon: "",
  discount_text: "",
});

const getInitialOrderData = (): IPlaceOrderResponse["data"] => ({
  id: 0,
  user_id: 0,
  order_id: "",
  order_reference_no: "",
  order_status: "",
});

const getInitialPayload = (): OrderHistoryPayload => ({
  page: 1,
  per_page: 10,
  filters: [] as string[],
});

const getInitialFilters = (): FilterState => ({
  period: "",
});

export const useOrderStore = defineStore("order", {
  state: () => ({
    orderSummary: getInitialData(),
    orderData: getInitialOrderData(),
    payload: getInitialPayload(),
    filters: getInitialFilters(),
  }),
  actions: {
    setOrderSummary(orderSummary: IOrderSummary) {
      this.orderSummary = orderSummary;
    },
    setOrderData(orderData: IPlaceOrderResponse["data"]) {
      this.orderData = orderData;
    },
    clearOrderStore() {
      this.orderSummary = getInitialData();
      this.orderData = getInitialOrderData();
      this.payload = getInitialPayload();
      this.filters = getInitialFilters();
    },
    setPayload(newPayload: OrderHistoryPayload) {
      this.payload = newPayload;
    },
    setFilters(newFilters: FilterState) {
      this.filters = newFilters;
    },
    setAppliedCode(code: string) {
      this.orderSummary.applied_code = code;
    },
    setCodeType(codeType: string) {
      this.orderSummary.code_type = codeType;
    },
    clearPayload() {
      this.payload = getInitialPayload();
      this.filters = getInitialFilters();
    },
  },
  getters: {
    getSubTotal: (state) => state.orderSummary.sub_total,
    getSubTotalInr: (state) => state.orderSummary.sub_total_inr,
    getCodeType: (state) => state.orderSummary.code_type,
    getCurrencyType: (state) => state.orderSummary.currency_type,
    getDiscountAmount: (state) => state.orderSummary.discount_amount,
    getTotalAmount: (state) => state.orderSummary.total_amount,
    getDiscountType: (state) => state.orderSummary.code_type,
    getRemainingBalance: (state) => state.orderSummary.remaining_balance,
    getCodeApplied: (state) => state.orderSummary.code_applied,
    getOrderId: (state) => state.orderData.id,
    getOrderReferenceNo: (state) => state.orderData.order_reference_no,
    getPayload: (state) => state.payload,
    getFilterState: (state) => state.filters,
    getAppliedCode: (state) => state.orderSummary.applied_code,
    getIsCashbackCoupon: (state) =>
      state.orderSummary.is_cashback_coupon === COMMON_CONSTANTS.POSITIVE_VALUE,
    getDiscountText: (state) => state.orderSummary.discount_text,
  },
});
